/** Shared labels and timing copy for scheduled / one-time tasks. */

export function isOnceTask(t: any): boolean {
  return t?.schedule_type === 'once'
}

export function parseScheduleConfig(task: any): Record<string, any> {
  const sc = task?.schedule_config
  if (sc && typeof sc === 'object' && !Array.isArray(sc)) return sc
  if (typeof sc === 'string') {
    try {
      const p = JSON.parse(sc)
      return p && typeof p === 'object' ? p : {}
    } catch {
      return {}
    }
  }
  return {}
}

export function scheduleLabel(t: any): string {
  if (t?.schedule_type === 'once') return 'One-time'
  if (t?.schedule_type === 'hourly') return 'Hourly'
  if (t?.schedule_type === 'daily') return 'Daily'
  if (t?.schedule_type === 'weekly') return 'Weekly'
  if (t?.schedule_type === 'monthly') return 'Monthly'
  if (t?.schedule_type === 'quarterly') return 'Quarterly'
  return t?.schedule_type || 'Scheduled'
}

function formatDateParts(isoOrDate: string | Date): { date: string; time: string } | null {
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate)
    if (Number.isNaN(d.getTime())) return null
    return {
      date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    }
  } catch {
    return null
  }
}

/** When the one-time task is scheduled to run (pending). */
export function formatOnceRunSchedule(t: any): string | null {
  if (!isOnceTask(t)) return null
  const cfg = parseScheduleConfig(t)
  const runDate = cfg.run_date
  const timeStr = cfg.time
  if (runDate) {
    try {
      const parts = String(runDate).split('-').map(Number)
      if (parts.length === 3 && !parts.some(Number.isNaN)) {
        const d = new Date(parts[0], parts[1] - 1, parts[2])
        const formatted = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        return timeStr ? `Runs once on ${formatted} at ${timeStr}` : `Runs once on ${formatted}`
      }
      return timeStr ? `Runs once on ${runDate} at ${timeStr}` : `Runs once on ${runDate}`
    } catch {
      return timeStr ? `Runs once on ${runDate} at ${timeStr}` : `Runs once on ${runDate}`
    }
  }
  if (t.next_run_at) {
    const parts = formatDateParts(t.next_run_at)
    if (parts) return `Runs once on ${parts.date} at ${parts.time}`
  }
  return 'Runs once'
}

/** True when a one-time task has already fired or was deactivated after scheduling. */
export function onceTaskCompleted(t: any): boolean {
  if (!isOnceTask(t)) return false
  if (t.last_run_at) return true
  return !t.is_active && !t.next_run_at
}

/** Subline timing for list cards: pending vs completed one-time tasks. */
export function formatOnceTaskTiming(t: any): string | null {
  if (!isOnceTask(t)) return null
  if (onceTaskCompleted(t)) {
    if (t.last_run_at) {
      const parts = formatDateParts(t.last_run_at)
      if (parts) return `Ran on ${parts.date} at ${parts.time}`
    }
    return 'Done'
  }
  return formatOnceRunSchedule(t)
}

export type OnceTaskBadge = 'scheduled' | 'completed' | 'cancelled'

export function onceTaskStatusBadge(t: any): { label: string; tone: OnceTaskBadge } {
  if (onceTaskCompleted(t)) return { label: 'Done', tone: 'completed' }
  if (t.is_active) return { label: 'Scheduled', tone: 'scheduled' }
  return { label: 'Cancelled', tone: 'cancelled' }
}

/**
 * Human category for grouping. Do not fall back to tool_name — raw MCP tool
 * ids like GET_ORDER_METADATA look ugly as section headers in My Tasks.
 */
export function taskCategoryLabel(t: any): string {
  return String(t?.category || '').trim()
}

export type TaskKindFilter = 'recurring' | 'once' | 'all'

/** Group tasks by category while preserving sort_order within each group. */
export function groupTasksByCategory<T extends { category?: string | null; tool_name?: string | null; sort_order?: number }>(
  tasks: T[],
): Array<{ title: string; tasks: T[] }> {
  const map = new Map<string, T[]>()
  for (const task of tasks) {
    const key = taskCategoryLabel(task)
    const list = map.get(key)
    if (list) list.push(task)
    else map.set(key, [task])
  }
  return Array.from(map.entries())
    .map(([title, groupTasks]) => ({
      title,
      tasks: groupTasks,
      minOrder: Math.min(...groupTasks.map((t) => t.sort_order ?? Number.MAX_SAFE_INTEGER)),
    }))
    .sort((a, b) => a.minOrder - b.minOrder || a.title.localeCompare(b.title))
    .map(({ title, tasks: groupTasks }) => ({ title, tasks: groupTasks }))
}

export function filterTasksByKind<T extends { schedule_type?: string; last_run_at?: string | null; is_active?: boolean; next_run_at?: string | null }>(
  tasks: T[],
  kind: TaskKindFilter,
  includeCompleted: boolean,
): T[] {
  return tasks.filter((t) => {
    const once = isOnceTask(t)
    if (kind === 'recurring' && once) return false
    if (kind === 'once' && !once) return false
    if (!includeCompleted && once && onceTaskCompleted(t)) return false
    return true
  })
}

export function nextRunRelative(iso?: string): string {
  if (!iso) return ''
  try {
    const t = new Date(iso).getTime()
    const now = Date.now()
    const diffMs = t - now
    if (diffMs <= 0) return 'soon'
    const minutes = Math.round(diffMs / 60000)
    if (minutes < 60) return `in ${minutes}m`
    const hours = Math.round(minutes / 60)
    if (hours < 48) return `in ${hours}h`
    const days = Math.round(hours / 24)
    return `in ${days}d`
  } catch {
    return ''
  }
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** Format HH:mm (24h) as a locale-friendly 12h clock string, e.g. "5:00 AM". */
export function formatScheduleTime(time?: string | null): string | null {
  if (!time || typeof time !== 'string') return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return time
  const hour24 = Number(match[1])
  const minute = Number(match[2])
  if (Number.isNaN(hour24) || Number.isNaN(minute) || hour24 > 23 || minute > 59) return time
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`
}

function ordinal(n: number): string {
  const abs = Math.abs(n)
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (abs % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

/**
 * Human-readable trigger sentence for the My Tasks table.
 * Examples: "Every day at 5:00 AM", "Every Monday at 8:00 AM", "First day of each quarter".
 */
export function triggerLabel(task: any): string {
  if (!task) return 'Scheduled'
  if (isOnceTask(task)) return formatOnceRunSchedule(task) || 'Runs once'

  const cfg = parseScheduleConfig(task)
  const timeLabel = formatScheduleTime(cfg.time)
  const atTime = timeLabel ? ` at ${timeLabel}` : ''
  const dayOfWeek = Number(cfg.day_of_week)
  const weekday =
    Number.isFinite(dayOfWeek) && dayOfWeek >= 0 && dayOfWeek <= 6
      ? WEEKDAY_NAMES[dayOfWeek]
      : 'Monday'
  const dayOfMonthRaw = Number(cfg.day_of_month)
  const dayOfMonth = Number.isFinite(dayOfMonthRaw) && dayOfMonthRaw >= 1 ? dayOfMonthRaw : 1

  switch (task.schedule_type) {
    case 'hourly': {
      if (cfg.time && /^\d{1,2}:(\d{2})$/.test(String(cfg.time).trim())) {
        const minute = String(cfg.time).trim().split(':')[1]
        return Number(minute) === 0 ? 'Every hour' : `Every hour at :${minute}`
      }
      return 'Every hour'
    }
    case 'daily':
      return `Every day${atTime}`
    case 'weekly':
      return `Every ${weekday}${atTime}`
    case 'monthly':
      return dayOfMonth === 1
        ? `First day of each month${atTime}`
        : `${ordinal(dayOfMonth)} of each month${atTime}`
    case 'quarterly':
      return dayOfMonth === 1
        ? `First day of each quarter${atTime}`
        : `${ordinal(dayOfMonth)} of each quarter${atTime}`
    default:
      return scheduleLabel(task)
  }
}

/** Compact relative label for a past run timestamp (chip strip). Empty when missing. */
export function formatRunRelativeTime(iso?: string | Date | null, nowMs: number = Date.now()): string {
  const s = formatRelativeTime(iso, nowMs)
  return s === '—' ? '' : s
}

/** Absolute run timestamp for chip hover/title, e.g. "Mon, Aug 12, 3:45 PM". */
export function formatRunAbsoluteTime(iso?: string | Date | null): string {
  if (!iso) return ''
  try {
    const d = iso instanceof Date ? iso : new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

/** Relative past timestamp for admin "Updated" column, e.g. "2d ago". */
export function formatRelativeTime(iso?: string | Date | null, nowMs: number = Date.now()): string {
  if (!iso) return '—'
  try {
    const t = iso instanceof Date ? iso.getTime() : new Date(iso).getTime()
    if (Number.isNaN(t)) return '—'
    const diffMs = Math.max(0, nowMs - t)
    const minutes = Math.floor(diffMs / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 48) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 14) return `${days}d ago`
    const weeks = Math.floor(days / 7)
    if (weeks < 8) return `${weeks}w ago`
    const months = Math.floor(days / 30)
    if (months < 18) return `${months}mo ago`
    const years = Math.floor(days / 365)
    return `${years}y ago`
  } catch {
    return '—'
  }
}

/**
 * Decide the default Task Library tab when the URL has no explicit `?tab=`.
 * Users with at least one scheduled task land on My Tasks; otherwise Task Library.
 */
export function resolveDefaultTaskLibraryTab(taskCount: number): 'my' | 'pre-built' {
  return taskCount > 0 ? 'my' : 'pre-built'
}
